import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * Herbessa FlyToCart
 * Premium bumblebee add-to-cart animation with contextual toast and rich particle trail.
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    this.classList.add('hb-fly-to-cart');

    const intersectionObserver = new IntersectionObserver((entries) => {
      /** @type {DOMRectReadOnly | null} */
      let sourceRect = null;

      /** @type {DOMRectReadOnly | null} */
      let destinationRect = null;

      entries.forEach((entry) => {
        if (entry.target === this.source) {
          sourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          destinationRect = entry.boundingClientRect;
        }
      });

      if (sourceRect && destinationRect) {
        this.#animate(sourceRect, destinationRect);
      }

      intersectionObserver.disconnect();
    });

    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);
  }

  /**
   * Real Herbessa bee image.
   * Trail is generated as separate JS particles.
   */
  #beeMarkup() {
    return `
      <div class="hb-fly-bee" aria-hidden="true">
        <img
          class="hb-fly-bee__img"
          src="https://cdn.shopify.com/s/files/1/2269/4189/files/bumblebee-herbessa-512.webp?v=1780420784"
          alt=""
          loading="eager"
          decoding="async"
        >
      </div>
    `;
  }

  /**
   * Formats cents as EUR.
   * @param {number} cents
   */
  #formatMoney(cents) {
    return new Intl.NumberFormat('sl-SI', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  /**
   * Gets current cart info from Shopify.
   */
  async #getCartInfo() {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return null;

      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Opens cart drawer from toast button.
   */
  #openCartDrawer() {
    const cartTrigger =
      document.querySelector('[data-cart-drawer-open]') ||
      document.querySelector('[aria-controls*="cart"]') ||
      document.querySelector('.header-actions__cart-icon') ||
      document.querySelector('a[href="/cart"]') ||
      document.querySelector('cart-drawer-component');

    if (cartTrigger instanceof HTMLElement) {
      cartTrigger.click();
    }
  }

  /**
   * Creates floating herb/gold particles behind the bee.
   * @param {number} x
   * @param {number} y
   */
  #createBeeParticle(x, y) {
    const particle = document.createElement('span');

    const variants = ['leaf-dark', 'leaf-light', 'leaf-olive', 'dust-gold', 'dust-cream'];
    const variant = variants[Math.floor(Math.random() * variants.length)];

    particle.className = `hb-bee-particle hb-bee-particle--${variant}`;

    const randomX = Math.round((Math.random() - 0.5) * 28);
    const randomY = Math.round((Math.random() - 0.5) * 24);
    const driftX = Math.round(-42 - Math.random() * 54);
    const driftY = Math.round((Math.random() - 0.5) * 46);
    const rotate = Math.round((Math.random() - 0.5) * 90);

    particle.style.left = `${x + randomX}px`;
    particle.style.top = `${y + randomY}px`;
    particle.style.setProperty('--hb-particle-x', `${driftX}px`);
    particle.style.setProperty('--hb-particle-y', `${driftY}px`);
    particle.style.setProperty('--hb-particle-rotate', `${rotate}deg`);

    document.body.appendChild(particle);

    window.setTimeout(() => {
      particle.remove();
    }, 1350);
  }

  /**
   * Shows premium Herbessa toast near the clicked button.
   * @param {DOMRectReadOnly} sourceRect
   */
  async #showToast(sourceRect) {
    const existingToast = document.querySelector('.hb-cart-toast');

    if (existingToast) {
      existingToast.remove();
    }

    const freeShippingThreshold = 5000;
    const cart = await this.#getCartInfo();

    let toastMessage = 'Vaša Herbessa košara se polni.';
    let progressPercent = 0;
    let isUnlocked = false;

    if (cart && typeof cart.total_price === 'number') {
      const remaining = freeShippingThreshold - cart.total_price;
      progressPercent = Math.min(100, Math.round((cart.total_price / freeShippingThreshold) * 100));

      if (remaining > 0) {
        toastMessage = `Do brezplačne dostave manjka še ${this.#formatMoney(remaining)}.`;
      } else {
        toastMessage = 'Brezplačna dostava odklenjena.';
        isUnlocked = true;
      }
    }

    const toast = document.createElement('div');
    toast.className = `hb-cart-toast hb-cart-toast--near-button hb-cart-toast--premium${
      isUnlocked ? ' hb-cart-toast--unlocked' : ''
    }`;

    toast.innerHTML = `
      <div class="hb-cart-toast__ornament" aria-hidden="true"></div>

      <div class="hb-cart-toast__top">
        <div class="hb-cart-toast__bee" aria-hidden="true">
          <img
            src="https://cdn.shopify.com/s/files/1/2269/4189/files/bumblebee-herbessa-512.webp?v=1780420784"
            alt=""
          >
        </div>

        <div class="hb-cart-toast__content">
          <span class="hb-cart-toast__eyebrow">HERBESSA</span>
          <strong>Dodano v košaro</strong>
          <span>${toastMessage}</span>
        </div>
      </div>

      <div class="hb-cart-toast__progress" aria-hidden="true">
        <span style="width: ${progressPercent}%;"></span>
      </div>

      <div class="hb-cart-toast__actions">
        <button class="hb-cart-toast__action hb-cart-toast__action--ghost" type="button" data-hb-toast-close>
          Nadaljuj z nakupom
        </button>
        <button class="hb-cart-toast__action hb-cart-toast__action--primary" type="button" data-hb-toast-cart>
          Odpri košaro
        </button>
      </div>

      <button class="hb-cart-toast__close" type="button" aria-label="Zapri">×</button>
    `;

    document.body.appendChild(toast);

    const toastWidth = Math.min(390, window.innerWidth - 32);
    const toastHeight = toast.offsetHeight || 190;
    const margin = 16;

    let left = sourceRect.left + sourceRect.width / 2 - toastWidth / 2;
    let top = sourceRect.bottom + 14;

    left = Math.max(margin, Math.min(left, window.innerWidth - toastWidth - margin));

    if (top + toastHeight + margin > window.innerHeight) {
      top = sourceRect.top - toastHeight - 14;
    }

    top = Math.max(margin, Math.min(top, window.innerHeight - toastHeight - margin));

    toast.style.setProperty('--hb-toast-left', `${left}px`);
    toast.style.setProperty('--hb-toast-top', `${top}px`);

    const closeToast = () => toast.remove();

    const closeButton = toast.querySelector('.hb-cart-toast__close');
    const continueButton = toast.querySelector('[data-hb-toast-close]');
    const cartButton = toast.querySelector('[data-hb-toast-cart]');

    closeButton?.addEventListener('click', closeToast, { once: true });
    continueButton?.addEventListener('click', closeToast, { once: true });

    cartButton?.addEventListener(
      'click',
      () => {
        closeToast();
        this.#openCartDrawer();
      },
      { once: true }
    );

    window.setTimeout(() => {
      toast.classList.add('hb-cart-toast--hide');

      window.setTimeout(() => {
        toast.remove();
      }, 350);
    }, 5200);
  }

  /**
   * Animates the bee along a soft curve toward the cart.
   * @param {DOMRectReadOnly} sourceRect
   * @param {DOMRectReadOnly} destinationRect
   */
  #animate = async (sourceRect, destinationRect) => {
    this.innerHTML = this.#beeMarkup();

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    const travelX = endPoint.x - startPoint.x;
    const travelY = endPoint.y - startPoint.y;

    const distance = Math.hypot(travelX, travelY);
    const duration = Math.min(3800, Math.max(2800, distance * 3.15));

    this.style.position = 'fixed';
    this.style.left = `${startPoint.x}px`;
    this.style.top = `${startPoint.y}px`;
    this.style.width = '112px';
    this.style.height = '80px';
    this.style.zIndex = '999999';
    this.style.pointerEvents = 'none';
    this.style.transform = 'translate(-50%, -50%)';
    this.style.willChange = 'transform, opacity';

    await yieldToMainThread();

    const lift = Math.min(-110, travelY * 0.35 - 70);

    const particleInterval = window.setInterval(() => {
      const rect = this.getBoundingClientRect();

      if (rect.width > 0 && rect.height > 0) {
        this.#createBeeParticle(rect.left + rect.width * 0.02, rect.top + rect.height * 0.58);
        this.#createBeeParticle(rect.left + rect.width * -0.04, rect.top + rect.height * 0.46);

        if (Math.random() > 0.45) {
          this.#createBeeParticle(rect.left + rect.width * -0.08, rect.top + rect.height * 0.66);
        }
      }
    }, 65);

    const flight = this.animate(
      [
        {
          transform: 'translate(-50%, -50%) translate(0px, 0px) scale(.66) rotate(-8deg)',
          opacity: 0,
          offset: 0,
        },
        {
          transform: `translate(-50%, -50%) translate(${travelX * 0.18}px, ${lift}px) scale(1) rotate(7deg)`,
          opacity: 1,
          offset: 0.22,
        },
        {
          transform: `translate(-50%, -50%) translate(${travelX * 0.58}px, ${
            travelY * 0.32 + lift * 0.35
          }px) scale(.94) rotate(-9deg)`,
          opacity: 1,
          offset: 0.62,
        },
        {
          transform: `translate(-50%, -50%) translate(${travelX}px, ${travelY}px) scale(.30) rotate(10deg)`,
          opacity: 0,
          offset: 1,
        },
      ],
      {
        duration,
        easing: 'cubic-bezier(.2,.78,.22,1)',
        fill: 'forwards',
      }
    );

    await Promise.allSettled([flight.finished]);

    window.clearInterval(particleInterval);

    await this.#showToast(sourceRect);
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}